import { useState, useMemo, useCallback } from 'react';
import { isNull } from 'lodash';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import useResizeObserver from 'use-resize-observer';
import { useTheme } from '@simplywallst/ui-core';
import { VisxText } from '@/components/VisxText';
import type { ComputedColumn, Entities } from './types';
import {
  mapSumPositiveValues,
  mapSumNegativeValues,
  calcColumnYValues,
} from './utils';
export interface Props {
  entities: Entities;
  entitiesFormatted: Record<string, string>;
  height: number;
  keys: string[];
  columnStackColors: string[];
  columnStackKeys: string[];
  initialSelectedColumn?: string;
  onSelectingColumn?: (key: string) => void;
}

export const VerticalStackBarChart = ({
  entities,
  entitiesFormatted,
  height,
  keys,
  columnStackColors,
  columnStackKeys,
  initialSelectedColumn,
  onSelectingColumn,
}: Props) => {
  const theme = useTheme();
  const [hoveringKey, setHoveringkey] = useState<string>(
    initialSelectedColumn || keys[0]
  );
  const offsetTop = theme.x5;
  const offsetBottom = theme.x5;
  const columnPadding = theme.x1;

  const { ref, width: rawWidth } = useResizeObserver<HTMLDivElement>();
  const width = rawWidth || 0;

  const sumColumnPosValues = mapSumPositiveValues(keys, entities);
  const sumColumnNegValues = mapSumNegativeValues(keys, entities);

  const max = Math.max(0, ...sumColumnPosValues, ...sumColumnNegValues);
  const min = Math.min(0, ...sumColumnPosValues, ...sumColumnNegValues);

  const xScale = scaleBand({
    domain: keys,
    range: [0, width],
  });

  const yScale = scaleLinear({
    domain: min === 0 && max === 0 ? [0, 1] : [min, max],
    range: [height, offsetTop],
  });

  const color = scaleOrdinal({
    domain: columnStackKeys,
    range: columnStackColors,
  });

  const zero = yScale(0) as number;
  const isNegativeAppear = min < 0;

  const computedColumns: ComputedColumn[] = useMemo(() => {
    return keys.map((key): ComputedColumn => {
      const columnValues = entities[key];
      const columnX = xScale(key) || 0;
      const columnWidth = width / keys.length;
      const formattedValue = entitiesFormatted[key];

      if (!columnValues) {
        return {
          key,
          x: columnX,
          width: columnWidth,
          top: zero,
          formattedValue,
          bars: [],
        };
      }

      const columnHeightValues = columnValues.map((value) => {
        return isNull(value) ? 0 : (yScale(value) as number) - zero;
      });

      const columnYValues = calcColumnYValues({
        columnValues,
        columnHeightValues,
      });

      const columnTop = Math.min(...columnYValues);

      return {
        key,
        x: columnX,
        width: columnWidth,
        top: columnTop + zero,
        formattedValue,
        bars: columnStackKeys.map((columnKey, columnIdx) => {
          const value = columnValues[columnIdx];
          return {
            key: columnKey,
            value,
            width: width / keys.length - columnPadding * 2,
            x: columnX + columnPadding,
            y: columnYValues[columnIdx] + zero,
            color: color(columnKey),
            height: Math.abs(columnHeightValues[columnIdx]),
          };
        }),
      };
    });
  }, [
    color,
    columnPadding,
    columnStackKeys,
    entities,
    entitiesFormatted,
    keys,
    width,
    xScale,
    yScale,
    zero,
  ]);

  const onHover = useCallback(
    (key: string) => {
      setHoveringkey(key);
      onSelectingColumn?.(key);
    },
    [onSelectingColumn]
  );

  return (
    <div ref={ref}>
      <svg
        width="100%"
        height={height + offsetBottom}
        shapeRendering="crispEdges"
      >
        {isNegativeAppear && (
          <rect
            x="0"
            y={zero}
            width="100%"
            height="1"
            fill={theme.color.white}
          />
        )}
        {computedColumns.map((column) => {
          const isHovering = hoveringKey === column.key;
          return (
            <g key={column.key}>
              <rect
                style={{ cursor: 'pointer' }}
                x={column.x}
                y="0"
                width={column.width}
                height={height + offsetBottom}
                fill={theme.color.textSolid}
                fillOpacity={isHovering ? 0.1 : 0}
                onMouseEnter={() => onHover(column.key)}
              />
              {column.bars.map(
                ({ key, color, height: barHeight, width: barWidth, x, y }) => {
                  return (
                    <rect
                      style={{ cursor: 'pointer' }}
                      key={`bar-${column.key}-${key}`}
                      x={x}
                      y={y}
                      width={barWidth}
                      height={barHeight}
                      fill={color}
                      onMouseEnter={() => onHover(column.key)}
                    />
                  );
                }
              )}
              <VisxText
                typography="xSmallStrong"
                y={column.top}
                x={column.x + columnPadding}
                dx={0}
                dy={-offsetTop / 2}
                lineHeight="1.5em"
                width={column.width}
                verticalAnchor="middle"
                fill={theme.color.textSoft}
              >
                {column.formattedValue}
              </VisxText>
              <VisxText
                typography="xSmall"
                y={height + offsetBottom}
                x={column.x + columnPadding}
                dy={-offsetBottom / 2}
                lineHeight="1.5em"
                width={column.width - columnPadding * 2}
                fill={theme.color.textSolid}
                verticalAnchor="middle"
                textAnchor="start"
                dx={0}
              >
                {column.key}
              </VisxText>
            </g>
          );
        })}
      </svg>
    </div>
  );
};
